import { FC, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { GenericLoading } from 'components/ui/Loader';
import { quotesAPI } from 'services/swagger-api';
import {
  MncapValidateTokenResponseDto,
  MncapValidateTokenResponseDtoStatusEnum
} from 'api/quote/models/MncapValidateTokenResponseDto';
import {
  MncapSendSmsCodeResponseDtoStatusEnum
} from 'api/quote/models/MncapSendSmsCodeResponseDto';
import {
  MncapVerifySmsCodeResponseDtoStatusEnum
} from 'api/quote/models/MncapVerifySmsCodeResponseDto';
import AerasReadingStep from './mncap-medical-validation/AerasReadingStep';
import SmsCodeStep from './mncap-medical-validation/SmsCodeStep';

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; titleKey: string; subtitleKey: string }
  | { kind: 'aeras'; data: MncapValidateTokenResponseDto; sendError: string | null; isSending: boolean }
  | {
      kind: 'sms';
      phoneMasked: string | null;
      sentAt: number;
      expiresInSeconds: number;
      verifyError: string | null;
      isVerifying: boolean;
      isResending: boolean;
      codeInvalidated: boolean;
    }
  | { kind: 'redirecting' };

const MncapMedicalValidation: FC = () => {
  const { t } = useTranslation(NsKey.MncapMedicalValidation);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  const redirectTo = useCallback((url: string) => {
    setPhase({ kind: 'redirecting' });
    window.location.replace(url);
  }, []);

  useEffect(() => {
    if (!token) {
      setPhase({
        kind: 'error',
        titleKey: 'MNCAP_MEDICAL_VALIDATION.TOKEN_ERROR.TITLE',
        subtitleKey: 'MNCAP_MEDICAL_VALIDATION.TOKEN_ERROR.SUBTITLE_INVALID'
      });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await quotesAPI.validateMncapMedicalToken({ token });
        if (cancelled) return;

        switch (response.status) {
          case MncapValidateTokenResponseDtoStatusEnum.AlreadyValidated:
            if (response.redirectUrl) {
              redirectTo(response.redirectUrl);
            } else {
              setPhase({
                kind: 'error',
                titleKey: 'MNCAP_MEDICAL_VALIDATION.TOKEN_ERROR.TITLE',
                subtitleKey: 'MNCAP_MEDICAL_VALIDATION.ERRORS.GENERIC'
              });
            }
            break;
          case MncapValidateTokenResponseDtoStatusEnum.Expired:
            setPhase({
              kind: 'error',
              titleKey: 'MNCAP_MEDICAL_VALIDATION.TOKEN_ERROR.TITLE',
              subtitleKey: 'MNCAP_MEDICAL_VALIDATION.TOKEN_ERROR.SUBTITLE_EXPIRED'
            });
            break;
          case MncapValidateTokenResponseDtoStatusEnum.Invalid:
            setPhase({
              kind: 'error',
              titleKey: 'MNCAP_MEDICAL_VALIDATION.TOKEN_ERROR.TITLE',
              subtitleKey: 'MNCAP_MEDICAL_VALIDATION.TOKEN_ERROR.SUBTITLE_INVALID'
            });
            break;
          case MncapValidateTokenResponseDtoStatusEnum.PendingValidation:
          default:
            setPhase({ kind: 'aeras', data: response, sendError: null, isSending: false });
            break;
        }
      } catch (e) {
        if (cancelled) return;
        setPhase({
          kind: 'error',
          titleKey: 'MNCAP_MEDICAL_VALIDATION.TOKEN_ERROR.TITLE',
          subtitleKey: 'MNCAP_MEDICAL_VALIDATION.ERRORS.GENERIC'
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, redirectTo]);

  const sendSmsCode = useCallback(
    async (phoneMasked: string | null) => {
      if (!token) {
        return { ok: false as const, errorKey: 'MNCAP_MEDICAL_VALIDATION.ERRORS.INVALID_TOKEN' };
      }
      try {
        const response = await quotesAPI.sendMncapMedicalSmsCode({
          mncapSendSmsCodeCommand: { token }
        });

        if (response.status === MncapSendSmsCodeResponseDtoStatusEnum.Sent) {
          setPhase({
            kind: 'sms',
            phoneMasked,
            sentAt: Date.now(),
            expiresInSeconds: response.expiresInSeconds ?? 120,
            verifyError: null,
            isVerifying: false,
            isResending: false,
            codeInvalidated: false
          });
          return { ok: true as const };
        }
        if (response.status === MncapSendSmsCodeResponseDtoStatusEnum.MaxResendsReached) {
          return { ok: false as const, errorKey: 'MNCAP_MEDICAL_VALIDATION.ERRORS.MAX_RESENDS_REACHED' };
        }
        if (response.status === MncapSendSmsCodeResponseDtoStatusEnum.InvalidToken) {
          return { ok: false as const, errorKey: 'MNCAP_MEDICAL_VALIDATION.ERRORS.INVALID_TOKEN' };
        }
        return { ok: false as const, errorKey: 'MNCAP_MEDICAL_VALIDATION.ERRORS.GENERIC' };
      } catch {
        return { ok: false as const, errorKey: 'MNCAP_MEDICAL_VALIDATION.ERRORS.GENERIC' };
      }
    },
    [token]
  );

  const handleAerasSubmit = useCallback(async () => {
    if (phase.kind !== 'aeras') return;
    const current = phase;
    setPhase({ ...current, isSending: true, sendError: null });
    const result = await sendSmsCode(current.data.phoneMasked ?? null);
    if (!result.ok) {
      setPhase({ ...current, isSending: false, sendError: t(result.errorKey) });
    }
  }, [phase, sendSmsCode, t]);

  const handleVerify = useCallback(
    async (code: string) => {
      if (phase.kind !== 'sms' || !token) return;
      setPhase({ ...phase, isVerifying: true, verifyError: null });
      try {
        const response = await quotesAPI.verifyMncapMedicalSmsCode({
          mncapVerifySmsCodeCommand: { token, code }
        });

        switch (response.status) {
          case MncapVerifySmsCodeResponseDtoStatusEnum.Valid:
            if (response.redirectUrl) {
              redirectTo(response.redirectUrl);
            } else {
              setPhase({
                ...phase,
                isVerifying: false,
                verifyError: t('MNCAP_MEDICAL_VALIDATION.ERRORS.GENERIC')
              });
            }
            break;
          case MncapVerifySmsCodeResponseDtoStatusEnum.Invalid: {
            const remaining = response.remainingAttempts ?? 0;
            const key =
              remaining <= 1
                ? 'MNCAP_MEDICAL_VALIDATION.ERRORS.INVALID_CODE_LAST'
                : 'MNCAP_MEDICAL_VALIDATION.ERRORS.INVALID_CODE';
            setPhase({
              ...phase,
              isVerifying: false,
              verifyError: t(key, { attempts: remaining })
            });
            break;
          }
          case MncapVerifySmsCodeResponseDtoStatusEnum.Expired:
            setPhase({
              ...phase,
              isVerifying: false,
              verifyError: t('MNCAP_MEDICAL_VALIDATION.ERRORS.CODE_EXPIRED'),
              codeInvalidated: true
            });
            break;
          case MncapVerifySmsCodeResponseDtoStatusEnum.MaxAttemptsReached:
            setPhase({
              ...phase,
              isVerifying: false,
              verifyError: t('MNCAP_MEDICAL_VALIDATION.ERRORS.MAX_ATTEMPTS_REACHED'),
              codeInvalidated: true
            });
            break;
        }
      } catch {
        setPhase({
          ...phase,
          isVerifying: false,
          verifyError: t('MNCAP_MEDICAL_VALIDATION.ERRORS.GENERIC')
        });
      }
    },
    [phase, token, redirectTo, t]
  );

  const handleResend = useCallback(async () => {
    if (phase.kind !== 'sms') return;
    setPhase({ ...phase, isResending: true, verifyError: null });
    const result = await sendSmsCode(phase.phoneMasked);
    if (!result.ok) {
      setPhase({
        ...phase,
        isResending: false,
        verifyError: t(result.errorKey)
      });
    }
  }, [phase, sendSmsCode, t]);

  if (phase.kind === 'loading') {
    return <GenericLoading loading isFullPage />;
  }

  if (phase.kind === 'redirecting') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="flex flex-col items-center gap-4">
          <GenericLoading loading />
          <p className="text-sm text-gray-600">
            {t('MNCAP_MEDICAL_VALIDATION.REDIRECTING.TITLE')}
          </p>
        </div>
      </div>
    );
  }

  if (phase.kind === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">{t(phase.titleKey)}</h1>
          <p className="text-sm text-gray-600 mb-4">{t(phase.subtitleKey)}</p>
          <p className="text-sm text-gray-500">
            {t('MNCAP_MEDICAL_VALIDATION.TOKEN_ERROR.CONTACT')}
          </p>
        </div>
      </div>
    );
  }

  if (phase.kind === 'aeras') {
    if (!phase.data.aerasFileUrl) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            <p className="text-sm text-gray-600">
              {t('MNCAP_MEDICAL_VALIDATION.AERAS.DOCUMENT_UNAVAILABLE')}
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <AerasReadingStep
          aerasFileUrl={phase.data.aerasFileUrl}
          aerasFileMimeType={phase.data.aerasFileMimeType}
          phoneMasked={phase.data.phoneMasked}
          onSubmit={handleAerasSubmit}
          isSubmitting={phase.isSending}
          errorMessage={phase.sendError}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 flex items-center">
      <SmsCodeStep
        phoneMasked={phase.phoneMasked}
        sentAt={phase.sentAt}
        expiresInSeconds={phase.expiresInSeconds}
        onSubmit={handleVerify}
        onResend={handleResend}
        isVerifying={phase.isVerifying}
        isResending={phase.isResending}
        errorMessage={phase.verifyError}
        codeInvalidated={phase.codeInvalidated}
      />
    </div>
  );
};

export default MncapMedicalValidation;
